import React, { useEffect, useState } from 'react';

import CRMApi from '../../../Api/CRM/CRMApi';
import User from '../../../Api/Models/User';
import Search from '../../elements/Search';
import Card from '../../elements/common/Card';
import TableLoader from '../../elements/common/TableLoader';
import Paginator from '../../elements/paginator';
import { PERMISSION } from '../../helpers/Permissions';
import useAutoPaginator, { FetchPaginatorParams } from '../../hooks/useAutoPaginator';

import UserTableDefault, { USER_TABLE_DEFAULT_ID } from './UserTableDefault';
import UserTableShort from './UserTableShort';

import { SelectOptions } from 'components/common/SelectInput';
import DownloadButton from 'crm/elements/DownloadButton';
import FilterByColumn, { Filter } from 'crm/elements/common/FilterByColumn';
import TableSettingsManager from 'crm/elements/common/TableSettingsManager';
import useOnce from 'crm/hooks/useOnce';
import useTableSettings from 'crm/hooks/useTableSettings';

type Props = {
    short?: boolean;
    cardTitle: string;
} & ({ source?: 'users'; relatedId?: never } | { source: 'affiliates' | 'subAffiliates'; relatedId: number });

export default function UsersList({ short = false, cardTitle, source = 'users', relatedId }: Readonly<Props>) {
    const [queryParams, setQueryParams] = useState<FetchPaginatorParams>();
    const settings = useTableSettings(USER_TABLE_DEFAULT_ID);

    const { items, updateItems, isLoading, auto } = useAutoPaginator<User>((page, params) => {
        setQueryParams(params);

        switch (source) {
            case 'affiliates':
                return CRMApi.affiliates.getUsersByAffiliateId(relatedId, page, { params });
            case 'subAffiliates':
                return CRMApi.subAffiliates.getUsersByAffiliateId(relatedId, page, { params });
            case 'users':
            default:
                return CRMApi.users.getAll(page, { params });
        }
    });

    const [countries, setCountries] = useState<SelectOptions>({});
    const [states, setStates] = useState<SelectOptions>({});
    const currentCountry = (auto.filter.columnFilters.country?.[1] ?? '') as string;

    useOnce(() => CRMApi.globals.getCountries().then(setCountries));

    useEffect(() => {
        if (currentCountry) {
            CRMApi.globals.getStates(currentCountry).then(setStates);
        }
    }, [currentCountry]);

    const tableHeader = (
        <>
            <span className="mr-3">{cardTitle}</span>
            {source === 'users' && (
                <DownloadButton
                    action="users-as-csv"
                    title="Export to CSV"
                    permissions={[PERMISSION.DOWNLOAD_USERS]}
                    defaultFilename="users.csv"
                    params={queryParams}
                />
            )}
        </>
    );

    return (
        <Card title={tableHeader}>
            <Search {...auto.search} />
            <FilterByColumn {...auto.filter}>
                {!short && [
                    <Filter key="country" column="country" type="select" options={countries} />,
                    <Filter key="state" column="state" type="select" options={states} />,
                    <Filter key="created_at" column="users.created_at" label="Created at" type="dateRange" />,
                ]}
            </FilterByColumn>
            {!short && <TableSettingsManager settings={settings} />}
            <TableLoader isLoading={isLoading}>
                {short ? (
                    <UserTableShort users={items} sortProps={auto.sort} />
                ) : (
                    <UserTableDefault users={items} update={updateItems} sortProps={auto.sort} />
                )}
                <Paginator {...auto.paginator} />
            </TableLoader>
        </Card>
    );
}
